<!--
  PlacementZoneDimensions -- SuperAdmin editor for a placement zone's
  printable envelope (default_max_width_in / default_max_height_in).

  Store Decorations redesign.
  Card: https://trello.com/c/oSOHJfg0

  Why this page exists
  ====================
  ADR 0065's per-zone printable dimensions are PROVISIONAL -- Jake
  confirms real values with the decorator / back-office (Dustin) later.
  They live on placement_zones.default_max_width_in /
  default_max_height_in (seeded by P1). This page lets a SuperAdmin
  correct them without a deploy, and is the durable place to circle
  back once the confirmed numbers land. The fit verdict
  (compute_verdict) reads these columns, so a correction here
  immediately sharpens every product's placement fit check.

  IA
  ==
  One row per placement zone (active + inactive; inactive grayed so a
  re-activated zone can still be tuned). Each row carries:

    * slug (e.g. left_chest)
    * display name (e.g. Left Chest)
    * status (Active / Inactive)
    * editable numeric input: max width (in)
    * editable numeric input: max height (in)
    * per-row Save button

  Saving is per-row (explicit Save; Enter in either input also saves).
  Both dims are sent together -- the backend requires both.

  Endpoints
  =========
  * GET   /api/admin/placement-zones
  * PATCH /api/admin/placement-zones/<id>/dimensions

  Mounted at /admin/placement-zones/dimensions via
  router/groups/superadmin.js. Gated on superadmin.settings.

  data-testid prefix: zone-dims-*
-->
<template>
    <section
        class="zone-dims"
        data-testid="zone-dims-root"
    >
        <header class="zone-dims__header">
            <h2 class="zone-dims__title">
                Placement zone dimensions
            </h2>
        </header>

        <p class="zone-dims__lede">
            The maximum printable envelope (width x height, in inches)
            for each placement zone. The product-decoration fit check
            (ok / tight / too big) is computed against these values.
        </p>

        <p
            class="zone-dims__provisional"
            data-testid="zone-dims-provisional-note"
        >
            <strong>Provisional (ADR 0065).</strong>
            These are working defaults from the ADR 0065 zone table --
            not yet confirmed with the decorator / back-office. Correct
            them here as the real per-zone limits are verified; the
            change takes effect without a deploy.
        </p>

        <div
            v-if="loading"
            data-testid="zone-dims-loading"
        >
            Loading...
        </div>

        <div
            v-else-if="!zones.length"
            class="zone-dims__empty"
            data-testid="zone-dims-empty"
        >
            No placement zones defined.
        </div>

        <table
            v-else
            class="zone-dims__table"
            data-testid="zone-dims-table"
        >
            <thead>
                <tr>
                    <th>Slug</th>
                    <th>Display name</th>
                    <th>Status</th>
                    <th>Max width (in)</th>
                    <th>Max height (in)</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="zone in zones"
                    :key="zone.id"
                    :class="{
                        'zone-dims__row--inactive': !zone.is_active,
                    }"
                    :data-testid="`zone-dims-row-${zone.id}`"
                >
                    <td>{{ zone.slug }}</td>
                    <td>{{ zone.display_name }}</td>
                    <td>
                        <span v-if="zone.is_active">Active</span>
                        <span
                            v-else
                            class="zone-dims__inactive-badge"
                        >
                            Inactive
                        </span>
                    </td>
                    <td>
                        <input
                            type="number"
                            min="0"
                            step="0.25"
                            :value="drafts[zone.id]?.width"
                            :disabled="saving"
                            :data-testid="`zone-dims-width-${zone.id}`"
                            @input="
                                onDraftInput(
                                    zone.id,
                                    'width',
                                    $event.target.value
                                )
                            "
                            @keydown.enter="onSave(zone.id)"
                        />
                    </td>
                    <td>
                        <input
                            type="number"
                            min="0"
                            step="0.25"
                            :value="drafts[zone.id]?.height"
                            :disabled="saving"
                            :data-testid="`zone-dims-height-${zone.id}`"
                            @input="
                                onDraftInput(
                                    zone.id,
                                    'height',
                                    $event.target.value
                                )
                            "
                            @keydown.enter="onSave(zone.id)"
                        />
                    </td>
                    <td>
                        <button
                            type="button"
                            class="tf-btn tf-btn--ghost tf-btn--sm"
                            :disabled="saving || !isDirty(zone.id)"
                            :data-testid="`zone-dims-save-${zone.id}`"
                            @click="onSave(zone.id)"
                        >
                            Save
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>

        <p
            v-if="successMsg"
            class="zone-dims__success"
            data-testid="zone-dims-success"
        >
            {{ successMsg }}
        </p>

        <p
            v-if="errorMsg"
            class="zone-dims__error"
            data-testid="zone-dims-error"
        >
            {{ errorMsg }}
        </p>
    </section>
</template>

<script>
import axios from "@/axios";

export default {
    name: "PlacementZoneDimensions",
    data() {
        return {
            zones: [],
            // Per-id { width: <string>, height: <string> } draft. Strings
            // so the input round-trips cleanly; parsed to Number on save.
            drafts: {},
            loading: false,
            saving: false,
            successMsg: "",
            errorMsg: "",
        };
    },
    mounted() {
        this.fetch();
    },
    methods: {
        _draftFor(zone) {
            return {
                width: String(zone.default_max_width_in),
                height: String(zone.default_max_height_in),
            };
        },
        async fetch() {
            this.loading = true;
            this.errorMsg = "";
            this.successMsg = "";
            try {
                const resp = await axios.get(
                    "/api/admin/placement-zones"
                );
                this.zones = resp.data.zones || [];
                const next = {};
                for (const z of this.zones) {
                    next[z.id] = this._draftFor(z);
                }
                this.drafts = next;
            } catch (err) {
                this.errorMsg =
                    err.response?.data?.error ||
                    "Failed to load placement zones.";
            } finally {
                this.loading = false;
            }
        },
        onDraftInput(zoneId, field, value) {
            const cur = this.drafts[zoneId] || { width: "", height: "" };
            this.drafts[zoneId] = { ...cur, [field]: value };
        },
        _zoneById(zoneId) {
            return this.zones.find((z) => z.id === zoneId);
        },
        isDirty(zoneId) {
            const zone = this._zoneById(zoneId);
            const draft = this.drafts[zoneId];
            if (!zone || !draft) return false;
            return (
                String(zone.default_max_width_in) !== String(draft.width) ||
                String(zone.default_max_height_in) !==
                    String(draft.height)
            );
        },
        async onSave(zoneId) {
            const draft = this.drafts[zoneId];
            if (!draft) return;
            if (!this.isDirty(zoneId)) return;

            const width = parseFloat(draft.width);
            const height = parseFloat(draft.height);
            // Client-side guard mirrors the backend contract: both must
            // parse to a positive number. The backend re-validates and
            // is the authority; this just avoids a wasted round-trip and
            // gives a local message.
            if (
                Number.isNaN(width) ||
                Number.isNaN(height) ||
                width <= 0 ||
                height <= 0
            ) {
                this.errorMsg =
                    "Both width and height must be positive numbers.";
                this.successMsg = "";
                return;
            }

            this.saving = true;
            this.errorMsg = "";
            this.successMsg = "";
            try {
                const resp = await axios.patch(
                    `/api/admin/placement-zones/${zoneId}/dimensions`,
                    {
                        default_max_width_in: width,
                        default_max_height_in: height,
                    }
                );
                // Reflect the canonical values back so the row settles to
                // exactly what the backend stored.
                const idx = this.zones.findIndex((z) => z.id === zoneId);
                if (idx !== -1) {
                    this.zones[idx] = resp.data;
                }
                this.drafts[zoneId] = this._draftFor(resp.data);
                this.successMsg = `Saved (${resp.data.slug}).`;
            } catch (err) {
                this.errorMsg =
                    err.response?.data?.error || "Save failed.";
                // Revert the draft to the canonical value so the inputs
                // don't strand a rejected value.
                const zone = this._zoneById(zoneId);
                if (zone) {
                    this.drafts[zoneId] = this._draftFor(zone);
                }
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.zone-dims {
    padding: 1.5rem;
}
.zone-dims__header {
    margin-bottom: 0.5rem;
}
.zone-dims__title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
}
.zone-dims__lede {
    color: #6b7280;
    max-width: 720px;
    margin-bottom: 0.75rem;
}
.zone-dims__provisional {
    max-width: 720px;
    margin-bottom: 1rem;
    padding: 0.5rem 0.75rem;
    background: #fef3c7;
    color: #92400e;
    border-radius: 6px;
    font-size: 0.875rem;
}
.zone-dims__table {
    width: 100%;
    border-collapse: collapse;
}
.zone-dims__table th,
.zone-dims__table td {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
    font-size: 0.875rem;
}
.zone-dims__table input[type="number"] {
    width: 6rem;
}
.zone-dims__row--inactive {
    color: var(--tf-fg-quiet);
}
.zone-dims__inactive-badge {
    padding: 0.125rem 0.5rem;
    background: #f3f4f6;
    color: #6b7280;
    border-radius: 999px;
    font-size: 0.75rem;
}
.zone-dims__empty {
    padding: 2rem;
    text-align: center;
    color: var(--tf-fg-4);
}
.zone-dims__success {
    color: #047857;
    margin-top: 0.75rem;
}
.zone-dims__error {
    color: #dc2626;
    margin-top: 0.75rem;
}
</style>
